import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getCandleRequestV2 } from '../../chartHelpers'
import { getCandlesBulkV2 } from '../../../../services/apis/candles'
import { Candle } from '../../../../proto/generated/candle-data-depot-common'
import { getHistogramRowCount, getMarketMinutesBetween, getVPFRTimeframeFromBucket } from './fixedRangeVolumeProfileConstants'
import { getTradingSessionsForDate } from '../../../../util/chart'
import { istToUTC } from '../../../../util/dateTime'
import { formatLargeNumber } from '../../../../util/screener'

export type FixedRangeVolumeProfileDrawingState = {
    p1?: Point,
    p2?: Point
}

type VolumeType = 'TOTAL' | 'UP/DOWN'
type Placement = 'left' | 'right'
export type RowLayout = 'NumberOfRows' | 'TicksPerRow'

export type FixedRangeVolumeProfileDrawingSettings = {
    rowSize: number,
    valueAreaVolumePercent: number,
    extendRight: boolean,
    volume: VolumeType
    rowLayout: RowLayout

    values: boolean,
    boxWidthPercent: number,
    placement: Placement,
    upVolumeColor: ColorValue,
    downVolumeColor: ColorValue,
    valueAreaUpVolumeColor: ColorValue,
    valueAreaDownVolumeColor: ColorValue,
    gridBoxColor: ColorValue,

    point1: Point,
    point2: Point,

    labelColor: ColorValue
}

type VolumeProfileBar = {
    price: number;
    totalVolume: number;
    upVolume: number;
    downVolume: number;
    valueArea: boolean;
};

interface VolumeProfileResult {
    profile: VolumeProfileBar[];
    pocPrice: number;
    valueAreaLow: number;
    valueAreaHigh: number;
}

class FixedRangeVolumeProfile extends DrawingBase<any, FixedRangeVolumeProfileDrawingSettings, FixedRangeVolumeProfileDrawingState> {
    loadingData: boolean = false
    rawData: Candle[] = []
    slope: number = 0
    intercept = 0
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
    }
    volumeProfileData: VolumeProfileResult | undefined

    loadIntialState() {
        this.getCandlesDataForPlotting()
    }

    onEditEnding() {
        this.getCandlesDataForPlotting()
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateLineAttributes()
        let { p1x, p2x, p1y, p2y } = this.scaledCoordinates
        if (!p1x || !p1y) return
        if(this.mode === 'DRAW' || this.editing) {
            ctx.globalAlpha = 1
            ctx.strokeStyle = this._chart.theme.palette.text.primary
            ctx.lineWidth = scaledCoordinate(1, this.verticalPixelRatio)
            ctx.beginPath()

            ctx.moveTo(p1x, p1y)
            ctx.lineTo(p2x, p2y)

            ctx.moveTo(p1x, 0)
            ctx.lineTo(p1x, this.canvasHeight)

            ctx.moveTo(p2x, 0)
            ctx.lineTo(p2x, this.canvasHeight)

            ctx.stroke()
        }
        if (!p2x || !p2y) return

        if(this.mode !== 'DRAW' && !this.editing && !this.loadingData) {
            this.drawVolumeProfileOverlay(scope)
        }
        ctx.closePath()
        ctx.globalAlpha = this._settings.labelColor.opacity
        ctx.fillStyle = this._settings.labelColor.color
        ctx.fill()
    };

    calculateLineAttributes() {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x
        }
    }

    calculateVolumeProfile() {
        const candles = this.rawData
        if (!candles.length) {
            this.volumeProfileData = { profile: [], pocPrice: 0, valueAreaLow: 0, valueAreaHigh: 0 }
            return
        }

        const tickSize = this._chart.securities?.[0]?.tickSize || 5
        const priceMultiplier = this._chart.securities?.[0]?.priceMultiplier ?? 100
        
        // Normalize prices using priceMultiplier
        const lows = candles.map(c => c.low / priceMultiplier);
        const highs = candles.map(c => c.high / priceMultiplier);

        const minPrice = Math.min(...lows);
        const maxPrice = Math.max(...highs);

        const numBins = getHistogramRowCount(maxPrice, minPrice, tickSize/priceMultiplier, this._settings.rowLayout, this._settings.rowSize)
        this._settings.rowSize
        const valueAreaPercent = this._settings.valueAreaVolumePercent / 100

        if(!this.editing) {
            if(this._state.p1.x < this._state.p2.x) {
                this._state.p1.y = maxPrice
                this._state.p2.y = minPrice
            } else {
                this._state.p1.y = minPrice
                this._state.p2.y = maxPrice
            }
        }
        const binSize = (maxPrice - minPrice) / numBins;

        // Initialize bins
        const bins = Array.from({ length: numBins }, () => ({
            totalVolume: 0,
            upVolume: 0,
            downVolume: 0,
        }));

        // Fill volume bins
        for (const candle of candles) {
            const low = candle.low / priceMultiplier;
            const high = candle.high / priceMultiplier;
            const vol = candle.volume;
            const up = candle.close > candle.open;

            const startBin = Math.floor((low - minPrice) / binSize);
            const endBin = Math.floor((high - minPrice) / binSize);
            const binsToFill = Math.max(1, endBin - startBin + 1);
            const volPerBin = vol / binsToFill;

            for (let i = startBin; i <= endBin && i < numBins && i >= 0; i++) {
                bins[i].totalVolume += volPerBin;
                if (up) bins[i].upVolume += volPerBin;
                else bins[i].downVolume += volPerBin;
            }
        }

        // Convert to array with prices
        const profile: VolumeProfileBar[] = bins.map((b, i) => ({
            price: (Number(((Number((minPrice + i * binSize + binSize / 2).toFixed(2)) * priceMultiplier) / tickSize).toFixed(0)) * tickSize) / priceMultiplier,
            totalVolume: b.totalVolume,
            upVolume: b.upVolume,
            downVolume: b.downVolume,
            valueArea: false,
        }));

        // Sort by volume descending
        const sorted = [...profile].sort((a, b) => b.totalVolume - a.totalVolume);

        // POC
        const pocPrice = sorted[0]?.price ?? 0;

        // Value Area logic (70% of total volume)
        const totalVolume = profile.reduce((acc, b) => acc + b.totalVolume, 0);
        const targetVolume = totalVolume * valueAreaPercent;

        let cumVolume = sorted[0].totalVolume;
        let lowerIndex = profile.findIndex(p => p.price === sorted[0].price);
        let upperIndex = lowerIndex;

        while (cumVolume < targetVolume) {
            const lowerNeighbor = lowerIndex > 0 ? profile[lowerIndex - 1] : null;
            const upperNeighbor =
                upperIndex < profile.length - 1 ? profile[upperIndex + 1] : null;

            const next =
                (upperNeighbor?.totalVolume ?? 0) > (lowerNeighbor?.totalVolume ?? 0)
                    ? upperNeighbor
                    : lowerNeighbor;

            if (!next) break;

            cumVolume += next.totalVolume;
            if (next === lowerNeighbor) lowerIndex--;
            else upperIndex++;
        }

        const valueAreaLow = profile[lowerIndex]?.price ?? minPrice;
        const valueAreaHigh = profile[upperIndex]?.price ?? maxPrice;

        // Mark VA bins
        for (const bar of profile) {
            if (bar.price >= valueAreaLow && bar.price <= valueAreaHigh) {
                bar.valueArea = true;
            }
        }
        this.volumeProfileData = { profile, pocPrice, valueAreaLow, valueAreaHigh };
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const latestTime = this._chart.getPrimaryData()?.slice(-1)?.[0]?.time
        const point: Point = {
            id: 'p1',
            x: (latestTime && time > latestTime) ? latestTime : time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
            if (!preview) {
                this.onDrawingEnd()
                this.getCandlesDataForPlotting()
            }
        }
        if (!preview) return { ...point }
    }

    recalculateHandles = () => {
        if(!this.editing) {
            this.calculateVolumeProfile()
        }
    }

    getCandlesDataForPlotting = async () => {
        this.loadingData = true
        const startIst = Math.min(this._state.p1.x as number, this._state.p2.x as number)
        const endIst = Math.max(this._state.p1.x as number, this._state.p2.x as number)
        const startTime = istToUTC(startIst) * 1000
        const endTime = istToUTC(endIst) * 1000
        const chartBucket = this._chart.getTimeBucket()
        const session = this._chart.calendarData
            ? getTradingSessionsForDate(Math.floor(startIst), this._chart.calendarData)[0]
            : undefined
        const sessionMinutes = session ? Math.max(0, Math.round((session.endTime - session.startTime) / 60)) : undefined
        const requireTimeBucket = getVPFRTimeframeFromBucket(
            {
                timeSeries: chartBucket.timeSeries,
                value: chartBucket.timeValue
            },
            getMarketMinutesBetween(
                new Date(startIst * 1000),
                new Date(endIst * 1000),
                session?.startTime,
                session?.endTime
            ),
            true,
            sessionMinutes
        )
        const candleRequest = getCandleRequestV2(
            1000,
            0,
            this._chart.securities[0],
            {
                ...chartBucket,
                timeSeries: requireTimeBucket.timeSeries,
                timeValue: requireTimeBucket.value
            },
            endTime,
            true,
            true
        )
        let data: Candle[] = []
        while (startTime < (data?.[0]?.startTimestamp ?? Infinity)) {
            const request = {...candleRequest}
            if(data.length) {
                request.referenceTime = data[0].startTimestamp
            }
            const candles = await getCandlesBulkV2([request])
            data = [...(candles.responses?.[0]?.backwardCandles ?? []), ...data]
        }
        const startIndex = data.findIndex(c => c.startTimestamp >= startTime)
        const endIndex = data.findIndex(c => c.startTimestamp >= endTime)
        const requiredCandles = data.slice(startIndex !== -1 ? startIndex : 0, endIndex !== -1 ? endIndex + 1 : data.length - 1)
        this.rawData = requiredCandles
        this.calculateVolumeProfile()
        this.loadingData = false
    }

    drawVolumeProfileOverlay(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        let { p1x, p2x, p1y, p2y } = this.scaledCoordinates

        // Draw grid box
        const left = Math.min(p1x, p2x)
        let right =  Math.max(p1x, p2x)

        if(this._settings.extendRight) {
            let latestTime = this._chart.getPrimaryData()?.slice(-1)?.[0]?.time
            let latestTimeCordinate = this.timeToCoordinate(latestTime!)
            right = scaledCoordinate(latestTimeCordinate!, this.horizontalPixelRatio)
        }
        const top = Math.min(p1y, p2y)
        const bottom = Math.max(p1y, p2y)
        ctx.fillStyle = this._settings.gridBoxColor.color
        ctx.globalAlpha = this._settings.gridBoxColor.opacity
        ctx.fillRect(left, top, right - left, bottom - top)

        const profile = this.volumeProfileData?.profile
        if (!profile || !profile.length) return

        // Draw POC line
        const pocY = this.priceToCoordinate( this.volumeProfileData?.pocPrice )
        const maxVol = Math.max(...profile.map(p => p.totalVolume))
        const bufferGap = scaledCoordinate(1, this.verticalPixelRatio)
        const textLeftBuffer = scaledCoordinate(4, this.horizontalPixelRatio)
        const textBottomBuffer = scaledCoordinate(2, this.verticalPixelRatio)
        const barHeight = ((bottom - top - (bufferGap * (profile.length + 1))) / profile.length)
        let lastYCoordinate = 0
        const textStartX = this._settings.placement === 'left' ? left + textLeftBuffer : right - textLeftBuffer
        const fontSize = scaledCoordinate(barHeight * 0.3, this.verticalPixelRatio)
        for (let i = 0; i < profile.length; i++) {
            const bar = profile[profile.length - 1 - i]
            if (pocY && bar.price === this.volumeProfileData?.pocPrice) {
                ctx.strokeStyle = this._chart.theme.palette.text.primary
                ctx.lineWidth = scaledCoordinate(2, this.verticalPixelRatio)
                ctx.globalAlpha = 1
                ctx.beginPath()
                ctx.moveTo(left, scaledCoordinate(pocY, this.verticalPixelRatio))
                ctx.lineTo(right, scaledCoordinate(pocY, this.verticalPixelRatio))
                ctx.stroke()
            }

            const y = scaledCoordinate(this.priceToCoordinate(bar.price), this.verticalPixelRatio)
            if (!y) continue
            lastYCoordinate = i===0 ? y - (barHeight / 2) + bufferGap : lastYCoordinate + barHeight + bufferGap


            const width = (bar.totalVolume / maxVol) * (this._settings.boxWidthPercent / 100) * (right - left) // max 100px width
            const xStart = left;

            const upRatio = bar.upVolume / bar.totalVolume;
            const downRatio = bar.downVolume / bar.totalVolume;

            const upWidth = width * upRatio;
            const downWidth = width * downRatio;

            // Down Volume (red)
            if(this._settings.volume === 'TOTAL') {
                ctx.globalAlpha = (bar?.valueArea ? this._settings.valueAreaUpVolumeColor : this._settings.upVolumeColor).opacity
                ctx.fillStyle = (bar?.valueArea ? this._settings.valueAreaUpVolumeColor : this._settings.upVolumeColor).color
            } else {
                ctx.globalAlpha = (bar?.valueArea ? this._settings.valueAreaDownVolumeColor : this._settings.downVolumeColor).opacity
                ctx.fillStyle = (bar?.valueArea ? this._settings.valueAreaDownVolumeColor : this._settings.downVolumeColor).color
            }
            ctx.fillRect(this._settings.placement === 'left' ? xStart + upWidth : right - upWidth - downWidth, lastYCoordinate, downWidth, barHeight)

            // Up Volume (green)
            ctx.globalAlpha = (bar?.valueArea ? this._settings.valueAreaUpVolumeColor : this._settings.upVolumeColor).opacity
            ctx.fillStyle = (bar?.valueArea ? this._settings.valueAreaUpVolumeColor : this._settings.upVolumeColor).color
            ctx.fillRect(this._settings.placement === 'left' ? xStart : right - upWidth, lastYCoordinate, upWidth, barHeight)

            if(this._settings.values && barHeight > 16) {
                ctx.globalAlpha = 1
                ctx.font = `${fontSize}px Arial`;
                ctx.fillStyle = this._chart.theme.palette.text.primary
                ctx.textAlign = this._settings.placement
                ctx.textBaseline = 'middle'
                const textValue = this._settings.volume === 'TOTAL' ? formatLargeNumber(bar.totalVolume) : this._settings.placement === 'left' ? `${formatLargeNumber(bar.upVolume)} x ${formatLargeNumber(bar.downVolume)}` : `${formatLargeNumber(bar.downVolume)} x ${formatLargeNumber(bar.upVolume)}`
                ctx.fillText(textValue, textStartX, lastYCoordinate + (barHeight/2) + textBottomBuffer )
            }
        }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state
        if (p1) {
            handles.push(p1)
        }


        p2 && handles.push(p2)
        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1x, p1y, p2x, p2y } = this.scaledCoordinates
        const minX = Math.min(p1x, p2x), maxX = Math.max(p1x, p2x)
        const minY = Math.min(p1y, p2y), maxY = Math.max(p1y, p2y)
        if (scaledX < minX || scaledX > maxX || scaledY < minY || scaledY > maxY) return null
        const line1Y = this.slope * scaledX + this.intercept

        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandles(points: Point[]) {
        this.editing = false
        // const latestTime = this._chart.getPrimaryData()?.slice(-1)?.[0]?.time
        // let alter = false
        // if(latestTime && (points.find(p => p.x > latestTime))) {
        //     alter = true
        // }
        // points.forEach((h) => {
        //     switch (h.id) {
        //         case 'p1':
        //             if(alter) {
        //                 h.x = latestTime as Time
        //             } else {
        //                 this._state.p1 = h
        //             }
        //             break
        //         case 'p2':
        //             if(alter) {
        //                 h.x = latestTime as Time
        //             } else {
        //                 this._state.p2 = h
        //             }
        //             break
        //         // case 'p3':
        //         //     this._state.p3 = h
        //         //     break
        //         // case 'p4':
        //         //     this._state.p4 = h
        //         //     break

        //     }
        // })
    }

    updateHandle = (id: string, time: Time, price: number, args: any, arg: any) => {
        const { p1, p2 } = this._state
        const latestTime = this._chart.getPrimaryData()?.slice(-1)?.[0]?.time
        if (!p1 || !p2) return
        const evaluatedTime = (latestTime && time > latestTime) ? latestTime : time
        switch (id) {
            case 'p1':
                p1.x = evaluatedTime,
                p1.y = price
                break
            case 'p2':
                p2.x = evaluatedTime,
                p2.y = price
                break
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        return [{
            id: 'start',
            p1: Math.min(p1.y, p2.y),
            p2: Math.max(p1.y, p2.y),
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }, {
            id: 'end',
            p1: Math.min(p1.y, p2.y),
            p2: Math.max(p1.y, p2.y),
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)

        if (!p1x || !p2x) return []
        let min: Time = p1.x, max: Time = p2.x
        if (p1x > p2x) {
            min = p2.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    onSettingsUpdate = (settings: Partial<FixedRangeVolumeProfileDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
            this.getCandlesDataForPlotting()
        }
        if (settings.point2) {
            this._state.p2 = settings.point2
            this.getCandlesDataForPlotting()
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<FixedRangeVolumeProfileDrawingSettings, SettingValues>[] => {
        return [{
            id: 'inputs',
            displayName: 'Inputs',
            visible: true,
            settings: [
                {
                    name: 'rowLayout',
                    label: 'Row Layout',
                    type: SettingType.SELECT,
                    options: [
                        { label: 'Number of Rows', value: 'NumberOfRows' },
                        { label: 'Ticks per Row', value: 'TicksPerRow' }
                    ],
                    defaultValue: 'NumberOfRows',
                    custom: true,
                    visible: true
                },
                {
                    name: 'rowSize',
                    label: 'Row Size',
                    type: SettingType.NUMBER,
                    defaultValue: 24,
                    range: [1, 100],
                    custom: true,
                    visible: true
                },
                {
                    name: 'volume',
                    label: 'Volume',
                    type: SettingType.SELECT,
                    options: [
                        { label: 'Up/Down', value: 'UP/DOWN' },
                        { label: 'Total', value: 'TOTAL' }
                    ],
                    defaultValue: 'UP/DOWN',
                    custom: true,
                    visible: true
                },
                {
                    name: 'valueAreaVolumePercent',
                    label: 'Value Area Volume',
                    type: SettingType.NUMBER,
                    defaultValue: 30,
                    range: [1, 100],
                    custom: true,
                    visible: true
                },
                {
                    name: 'extendRight',
                    label: 'Extend Right',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                }
            ]
        },
        {
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'values',
                    label: 'Values',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                },
                {
                    name: 'boxWidthPercent',
                    label: 'Width (% of the box)',
                    type: SettingType.NUMBER,
                    defaultValue: 30,
                    range: [0, 100],
                    custom: true,
                    visible: true
                },
                {
                    name: 'placement',
                    label: 'Placement',
                    type: SettingType.SELECT,
                    options: [
                        { label: 'Left', value: 'left' },
                        { label: 'Right', value: 'right' }
                    ],
                    defaultValue: 'left',
                    custom: true,
                    visible: true
                },
                {
                    name: 'upVolumeColor',
                    label: 'Up Volume',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#4184f3',
                        opacity: 0.5
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'downVolumeColor',
                    label: 'Down Volume',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#ff5252',
                        opacity: 0.5
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'valueAreaUpVolumeColor',
                    label: 'Value Area Up Volume',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#4184f3',
                        opacity: 0.75
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'valueAreaDownVolumeColor',
                    label: 'Value Area Down Volume',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#ff5252',
                        opacity: 0.75
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'gridBoxColor',
                    label: 'Histogram Box',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#4184f3',
                        opacity: 0.06
                    },
                    custom: true,
                    visible: true
                },

                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#4184f3',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
            ]
        },
        {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1 (bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'a',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: false
                    }
                },
                {
                    name: 'point2',
                    label: '#2 (bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'b',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: false
                    }
                }
            ]
        }
    ]
    }

    getSettingsValues(): FixedRangeVolumeProfileDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        if (this._state.p2) {
            settings.point2 = this._state.p2
        }
        return settings
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof FixedRangeVolumeProfileDrawingSettings)[] = []

        // settings.forEach((s) => {
        //     if (s.id === 'style') {
        //         miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
        //     }
        // })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'fixedRangeVolumeProfile'
    }
}

export default FixedRangeVolumeProfile